import { useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import FaqAccordion from "@/components/seo/FaqAccordion";
import CtaBlock from "@/components/seo/CtaBlock";
import RelatedGrid from "@/components/seo/RelatedGrid";
import useSeo from "@/hooks/useSeo";
import { getCityBySlug, CITIES } from "@/data/seo/cities";
import { SERVICES } from "@/data/seo/services";
import {
  breadcrumbSchema,
  faqSchema,
  localBusinessForCity,
  canonicalFor,
} from "@/lib/seoHelpers";

export default function CityPage() {
  const { slug } = useParams();
  const city = getCityBySlug(slug);

  const canonical = canonicalFor(`/locations/${slug}`);
  const crumbs = city
    ? [
        { label: "Home", href: "/" },
        { label: "Locations", href: "/locations" },
        { label: city.city, href: `/locations/${slug}` },
      ]
    : [];

  useSeo({
    title: city?.metaTitle,
    description: city?.metaDescription,
    canonical,
    ogImage: city?.heroImage,
  });

  if (!city) return <Navigate to="/404" replace />;

  const schemas = [
    breadcrumbSchema(crumbs),
    faqSchema(city.faqs),
    localBusinessForCity({
      city: city.city,
      country: city.country,
      slug: city.slug,
    }),
  ];

  // Related: top 4 services + 3 other cities in same category
  const serviceLinks = SERVICES.slice(0, 4).map((s) => ({
    href: `/services/${s.slug}`,
    label: `${s.title} in ${city.city}`,
    eyebrow: s.eyebrow,
    description: `Bespoke ${s.title.toLowerCase()} for couples in ${city.city}.`,
  }));

  const otherCities = CITIES.filter(
    (c) => c.type === city.type && c.slug !== city.slug
  )
    .slice(0, 3)
    .map((c) => ({
      href: `/locations/${c.slug}`,
      label: `Wedding Invitations in ${c.city}`,
      eyebrow: c.type === "india" ? "India" : "International",
      description: `Premium digital wedding invitations for ${c.city} couples.`,
    }));

  return (
    <SeoPageLayout schemas={schemas}>
      <article
        data-testid={`city-page-${slug}`}
        className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-14">
          <span className="overline text-[var(--iw-gold)]">
            {city.type === "india" ? "India · " : "International · "}
            {city.region}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Wedding Invitations in {city.city}
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            {city.intro}
          </p>
        </div>

        <div
          className="relative aspect-[16/9] mb-14 overflow-hidden"
          style={{ border: "1px solid rgba(212,175,55,0.2)" }}
        >
          <img
            src={city.heroImage}
            alt={`Wedding invitations in ${city.city} — InvitationWala`}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="space-y-14 md:space-y-20">
          {city.sections.map((sec, i) => (
            <section key={i} data-testid={`city-section-${i}`}>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-[var(--iw-gold)]" />
                <h2
                  className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {sec.h2}
                </h2>
              </div>
              {sec.paragraphs?.map((p, pi) => (
                <p
                  key={pi}
                  className="text-[var(--iw-muted)] leading-relaxed text-base md:text-lg mb-5"
                >
                  {p}
                </p>
              ))}
              {sec.bullets && (
                <ul className="mt-5 space-y-3">
                  {sec.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-[var(--iw-muted)] leading-relaxed"
                    >
                      <span className="text-[var(--iw-gold)] mt-1 shrink-0">
                        ✦
                      </span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <section className="mt-20 md:mt-28" data-testid="city-faq-section">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-4xl leading-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Frequently Asked Questions
            </h2>
          </div>
          <FaqAccordion faqs={city.faqs} testid={`faq-city-${slug}`} />
        </section>

        <CtaBlock
          title={`Design your ${city.city} wedding invitation`}
          subtitle="WhatsApp us your wedding details and we'll share a transparent quote within an hour."
          prefilledMessage={`Hi InvitationWala, I'm from ${city.city} and I'd like to enquire about a wedding invitation.`}
          testid={`cta-city-${slug}`}
        />

        <RelatedGrid
          title="Services in this city"
          items={serviceLinks}
          testid="city-service-links"
        />
        <RelatedGrid
          title={
            city.type === "india"
              ? "Other Indian cities we serve"
              : "Other international cities we serve"
          }
          items={otherCities}
          testid="other-cities-links"
        />
      </article>
    </SeoPageLayout>
  );
}
